import React, { forwardRef } from 'react';
import './UIInput.scss';

export interface UIInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Варіант стилю інпута
   */
  variant?: 'default' | 'error' | 'success';

  /**
   * Розмір інпута
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Повний width батьківського контейнера
   */
  fullWidth?: boolean;

  /**
   * Текст помилки (показується під інпутом)
   */
  error?: string;

  /**
   * Лейбл над інпутом
   */
  label?: string;

  /**
   * Показувати кнопку очистки
   */
  showClearButton?: boolean;

  /**
   * Колбек для очистки значення
   */
  onClear?: () => void;

  /**
   * Обробник натискання клавіш
   */
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const mockProps: Partial<UIInputProps> = {
  placeholder: 'Placeholder',
  variant: 'default',
  size: 'medium',
  disabled: false,
  fullWidth: false,
  showClearButton: false,
};

const baseClass = 'UIInput';

const UIInput = forwardRef<HTMLInputElement, UIInputProps>(
  (
    {
      variant = mockProps.variant,
      size = mockProps.size,
      className = '',
      fullWidth = mockProps.fullWidth,
      error,
      label,
      placeholder = mockProps.placeholder,
      disabled = mockProps.disabled,
      showClearButton = mockProps.showClearButton,
      onClear,
      onKeyPress,
      ...props
    },
    ref,
  ) => {
    const inputClasses = [
      baseClass,
      `${baseClass}--${variant}`,
      `${baseClass}--${size}`,
      fullWidth && `${baseClass}--full-width`,
      error && `${baseClass}--error`,
      showClearButton && `${baseClass}--with-clear`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const containerClasses = [
      `${baseClass}__container`,
      fullWidth && `${baseClass}__container--full-width`,
    ]
      .filter(Boolean)
      .join(' ');

    const wrapperClasses = [
      `${baseClass}__wrapper`,
      fullWidth && `${baseClass}__wrapper--full-width`,
    ]
      .filter(Boolean)
      .join(' ');

    const handleClearClick = () => {
      if (onClear) {
        onClear();
      }
    };

    return (
      <div className={containerClasses}>
        {label && <label className={`${baseClass}__label`}>{label}</label>}

        <div className={wrapperClasses}>
          <input
            ref={ref}
            className={inputClasses}
            placeholder={placeholder}
            disabled={disabled}
            onKeyPress={onKeyPress}
            {...props}
          />

          {showClearButton && (
            <button
              type="button"
              className={`${baseClass}__clear-button`}
              onClick={handleClearClick}
              disabled={disabled}
              aria-label="Clear input"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className={`${baseClass}__clear-icon`}
              >
                <path
                  d="M12 4L4 12M4 4L12 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        {error && <span className={`${baseClass}__error-text`}>{error}</span>}
      </div>
    );
  },
);

UIInput.displayName = 'UIInput';

export default UIInput;